import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trophy, ThumbsUp, ThumbsDown, Heart, MessageSquare, Star, Crown, Award, Medal } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

const MONTHS = [
  { v: 1, l: "Janeiro" }, { v: 2, l: "Fevereiro" }, { v: 3, l: "Março" },
  { v: 4, l: "Abril" }, { v: 5, l: "Maio" }, { v: 6, l: "Junho" },
  { v: 7, l: "Julho" }, { v: 8, l: "Agosto" }, { v: 9, l: "Setembro" },
  { v: 10, l: "Outubro" }, { v: 11, l: "Novembro" }, { v: 12, l: "Dezembro" },
];
const ROLE_LABEL = { admin: "Admin", reception: "Recepção", trainer: "Personal", finance: "Financeiro" };

export default function StaffRatings() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [mode, setMode] = useState("month"); // month | year
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = mode === "month" ? `year=${year}&month=${month}` : `year=${year}`;
      const { data } = await api.get(`/staff-ratings/report?${params}`);
      setReport(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, mode]);

  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
  const ranking = report?.ranking || [];
  const podiumIcons = [Crown, Award, Medal];
  const podiumColors = ["text-amber-500", "text-slate-400", "text-orange-500"];

  return (
    <div className="space-y-4" data-testid="staff-ratings-page">
      <PageHeader
        title="Avaliações dos Funcionários"
        subtitle="Ranking mensal e anual das reações dos alunos sobre a equipe"
        actions={
          <div className="flex items-center gap-2">
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="w-32" data-testid="staff-ratings-mode"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="month">Mensal</SelectItem><SelectItem value="year">Anual</SelectItem></SelectContent>
            </Select>
            {mode === "month" && (
              <Select value={String(month)} onValueChange={(v)=>setMonth(parseInt(v))}>
                <SelectTrigger className="w-36" data-testid="staff-ratings-month"><SelectValue/></SelectTrigger>
                <SelectContent>{MONTHS.map(m => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Select value={String(year)} onValueChange={(v)=>setYear(parseInt(v))}>
              <SelectTrigger className="w-24" data-testid="staff-ratings-year"><SelectValue/></SelectTrigger>
              <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 bg-gradient-to-br from-rose-50 to-white border-rose-200">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-rose-600 font-bold"><Star className="w-4 h-4"/>Total de avaliações</div>
          <div className="font-heading text-3xl font-black text-rose-600 mt-1">{report?.total_ratings || 0}</div>
        </Card>
        <Card className="p-4 border-rose-200">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-rose-500 font-bold"><Heart className="w-4 h-4"/>Corações</div>
          <div className="font-heading text-3xl font-black text-rose-500 mt-1">{ranking.reduce((s, r)=>s+(r.heart||0), 0)}</div>
        </Card>
        <Card className="p-4 border-emerald-200">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-600 font-bold"><ThumbsUp className="w-4 h-4"/>Gostei</div>
          <div className="font-heading text-3xl font-black text-emerald-600 mt-1">{ranking.reduce((s, r)=>s+(r.like||0), 0)}</div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-bold"><ThumbsDown className="w-4 h-4"/>Não gostei</div>
          <div className="font-heading text-3xl font-black text-slate-500 mt-1">{ranking.reduce((s, r)=>s+(r.dislike||0), 0)}</div>
        </Card>
      </div>

      <Card className="p-5 border-slate-200" data-testid="staff-ratings-ranking">
        <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-amber-500"/><h3 className="font-heading text-lg font-bold text-slate-900">Ranking · Score = ❤️×3 + 👍 − 👎×2</h3></div>
        {loading && <div className="text-center py-8 text-slate-400">Carregando...</div>}
        {!loading && ranking.length === 0 && <div className="text-center py-12 text-slate-400">Nenhuma avaliação registrada neste período.</div>}
        <div className="space-y-2">
          {ranking.map((r, i) => {
            const Icon = podiumIcons[i];
            return (
              <div key={r.staff_id} className={`flex items-center gap-4 p-4 rounded-lg border ${i === 0 ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={`ranking-row-${r.staff_id}`}>
                <div className="w-8 text-center">
                  {Icon ? <Icon className={`w-6 h-6 ${podiumColors[i]}`}/> : <span className="text-slate-400 font-bold">{i + 1}º</span>}
                </div>
                {r.photo ? (
                  <img src={r.photo} alt={r.staff_name} className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"/>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-500">{(r.staff_name || "?")[0]}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-slate-900 truncate">{r.staff_name}</div>
                  <div className="text-xs text-slate-500">{ROLE_LABEL[r.staff_role] || r.staff_role} · {r.total} avaliação(ões)</div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1 text-rose-500 font-bold" title="Corações"><Heart className="w-4 h-4"/>{r.heart || 0}</div>
                  <div className="flex items-center gap-1 text-emerald-600 font-bold" title="Gostei"><ThumbsUp className="w-4 h-4"/>{r.like || 0}</div>
                  <div className="flex items-center gap-1 text-slate-500 font-bold" title="Não gostei"><ThumbsDown className="w-4 h-4"/>{r.dislike || 0}</div>
                  <div className="w-16 text-right font-heading text-xl font-black text-amber-600" data-testid={`ranking-score-${r.staff_id}`}>{r.score}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {ranking.some(r => r.comments && r.comments.length > 0) && (
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-4"><MessageSquare className="w-5 h-5 text-sky-600"/><h3 className="font-heading text-lg font-bold text-slate-900">Comentários dos alunos</h3></div>
          <div className="space-y-4">
            {ranking.filter(r => r.comments?.length).map(r => (
              <div key={r.staff_id}>
                <div className="font-bold text-slate-900 mb-2">{r.staff_name}</div>
                <div className="space-y-2">
                  {r.comments.map((c, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`comment-${r.staff_id}-${i}`}>
                      <div className="text-lg">{c.reaction === "heart" ? "❤️" : c.reaction === "like" ? "👍" : "👎"}</div>
                      <div className="flex-1">
                        <div className="text-xs text-slate-500">{c.student_name} · {new Date(c.created_at).toLocaleDateString("pt-BR")}</div>
                        <div className="text-sm text-slate-800 mt-0.5">{c.comment}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
